import type { SupabaseClient } from "@supabase/supabase-js";

// Companion punch-location read model: public.management_timeclock_location_points (G2b).
//
// The primary timeclock report (`management_timeclock_log`, see ./timeclock.ts) stays
// COORDINATE-FREE on purpose: a broad table view of every punch must not be a location export.
// This module is the deliberate exception behind Show on map and the bounded Focus punch story.
// It is the ONLY console reader of punch coordinates. Nothing here feeds the CSV export (G5a).
//
// The companion view is owned by the DB lane and may not exist on a given host yet, so every
// failure mode below resolves to a value the UI can state honestly. Nothing invents a
// coordinate, and no PostgREST/Postgres error text ever reaches the operator.

export interface TimeclockLocationPoint {
  timePunchId: string;
  lat: number;
  lng: number;
  gpsAccuracyMeters: number | null;
}

/**
 * Three honest answers, not two. "We have no point for this punch" and "we could not ask" are
 * different facts, and the log row alone cannot distinguish them. A punch can read
 * `gps_captured` while the companion view is missing on this host. Collapsing them into a bare
 * null would make a deployment gap look like a guard who did not share location.
 */
export type TimeclockLocationLookup =
  | { status: "found"; point: TimeclockLocationPoint }
  | { status: "missing" }
  | { status: "unavailable" };

export type TimeclockLocationBatchLookup =
  | { status: "found"; points: TimeclockLocationPoint[] }
  | { status: "unavailable" };

export const TIMECLOCK_LOCATION_VIEW = "management_timeclock_location_points";

/** Explicit column list: never `*`. The view is elevated, so widening is a deliberate edit. */
export const TIMECLOCK_LOCATION_COLUMNS = "time_punch_id,lat,lng,gps_accuracy_meters";
export const TIMECLOCK_LOCATION_BATCH_LIMIT = 200;

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Map a companion row → point, or null when it cannot be plotted honestly. */
export function mapTimeclockLocationPoint(
  raw: Record<string, unknown>,
): TimeclockLocationPoint | null {
  const timePunchId = typeof raw.time_punch_id === "string" ? raw.time_punch_id : "";
  const lat = asFiniteNumber(raw.lat);
  const lng = asFiniteNumber(raw.lng);
  if (timePunchId.length === 0 || lat === null || lng === null) {
    return null;
  }
  // The view is specified to project only in-range coordinates, but a client that would fly a
  // map to (0, 999) on bad data is a client that lies about where someone was.
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return {
    timePunchId,
    lat,
    lng,
    gpsAccuracyMeters: asFiniteNumber(raw.gps_accuracy_meters),
  };
}

/**
 * Fetch the one location point for a punch. Called only when an operator clicks Show on map:
 * never on page load, never for a page of rows.
 *
 * Fails closed: a missing view, an RLS refusal, and a network drop are indistinguishable to the
 * client and all resolve to `unavailable`. RLS remains the authority; the account filter is the
 * same defence-in-depth scoping every other console reader applies.
 */
export async function fetchTimeclockLocationPoint(
  client: SupabaseClient,
  accountId: string,
  timePunchId: string,
): Promise<TimeclockLocationLookup> {
  if (accountId.length === 0 || timePunchId.length === 0) {
    return { status: "missing" };
  }
  try {
    const { data, error } = await client
      .from(TIMECLOCK_LOCATION_VIEW)
      .select(TIMECLOCK_LOCATION_COLUMNS)
      .eq("account_id", accountId)
      .eq("time_punch_id", timePunchId)
      .limit(1);
    if (error) {
      return { status: "unavailable" };
    }
    const row = (Array.isArray(data) ? data : [])[0];
    if (typeof row !== "object" || row === null) {
      return { status: "missing" };
    }
    const point = mapTimeclockLocationPoint(row as Record<string, unknown>);
    return point === null ? { status: "missing" } : { status: "found", point };
  } catch {
    // A thrown client (offline, aborted fetch) is the same operator-visible fact as a rejected
    // read: we could not ask. Swallowing the cause here is what keeps driver text off the UI.
    return { status: "unavailable" };
  }
}

/**
 * Fetch a bounded allowlist of punch points for one active Focus subject. The caller supplies
 * punch ids from an account and assignment scoped timeclock read. This adapter remains the only
 * module that names the elevated coordinate view.
 */
export async function fetchTimeclockLocationPoints(
  client: SupabaseClient,
  accountId: string,
  timePunchIds: readonly string[],
): Promise<TimeclockLocationBatchLookup> {
  const ids = [...new Set(timePunchIds.filter((id) => id.length > 0))]
    .slice(0, TIMECLOCK_LOCATION_BATCH_LIMIT);
  if (accountId.length === 0 || ids.length === 0) {
    return { status: "found", points: [] };
  }
  try {
    const { data, error } = await client
      .from(TIMECLOCK_LOCATION_VIEW)
      .select(TIMECLOCK_LOCATION_COLUMNS)
      .eq("account_id", accountId)
      .in("time_punch_id", ids)
      .limit(ids.length);
    if (error || !Array.isArray(data)) {
      return { status: "unavailable" };
    }
    return {
      status: "found",
      points: (data as Array<Record<string, unknown>>)
        .map((row) => mapTimeclockLocationPoint(row))
        .filter((point): point is TimeclockLocationPoint => point !== null),
    };
  } catch {
    return { status: "unavailable" };
  }
}
